import { Fragment } from 'react'
import Box from '@mui/material/Box'
import type { FormNode, GroupLayout } from '../../../../types/form.types'
import { FormGridRow } from './FormGridRow'

export interface FormGridProps {
  nodes: FormNode[]
  renderNode: (node: FormNode) => React.ReactNode
  /** When provided, called before each row and after the last row for drop zones (insertIndex 0..rows.length) */
  renderDropZone?: (insertIndex: number) => React.ReactNode
  /** Layout for group children (form root without layout = vertical grid by rows) */
  groupLayout?: GroupLayout
  /** Vertical stack + row gap (card-chrome forms: 16px). */
  rowGap?: number
  gridRowAlignItems?: 'center' | 'flex-start' | 'stretch'
  /** App mode: Table rows grow so the tabular grid is the scroll host. */
  fillRemaining?: boolean
}

function isHorizontalGroupLayout(layout: GroupLayout | undefined): boolean {
  return (
    layout === 'horizontalAlways' ||
    layout === 'horizontalIfPossible' ||
    layout === 'screenAware'
  )
}

/**
 * Sizing for a horizontal-group child (spec 2026-08-07, owner-approved).
 * Rows are evaluated top-down — first match wins:
 *   1. explicit numeric width — fixed, for EVERY node type (minWidth 0 so the
 *      configurator's pixels beat the control's intrinsic minimum);
 *   2. Button / CommandBar / Table — content width (a stretched button is
 *      wrong; a squeezed table clips under TableElement's overflow hidden);
 *   3. horizontalAlways — equal shares; the 120px floor is what re-arms the
 *      container's overflowX ("always horizontal, scroll if needed");
 *   4. horizontalIfPossible — the 240px basis IS the wrap threshold;
 *   5. screenAware — responsive by necessity: the container flips to a column
 *      on xs, where a flex basis would govern HEIGHT and the container's
 *      alignItems 'flex-start' blocks cross-axis stretch, so the xs branch
 *      opts out of flex sizing and takes the full width explicitly.
 */
function horizontalChildSx(node: FormNode, groupLayout: GroupLayout | undefined) {
  if (typeof node.width === 'number') {
    return { flex: `0 0 ${node.width}px`, minWidth: 0 }
  }
  if (node.type === 'Button' || node.type === 'CommandBar' || node.type === 'Table') {
    return { flex: '0 0 auto', minWidth: 0 }
  }
  if (groupLayout === 'horizontalAlways') {
    return { flex: '1 1 0', minWidth: 120 }
  }
  if (groupLayout === 'horizontalIfPossible') {
    return { flex: '1 1 240px', minWidth: 0 }
  }
  // Emit xs as base styles (not `{ xs, sm }` objects): MUI puts object-form
  // xs inside `@media (min-width:0px)`, which jsdom never applies — computed
  // style would stay unset and the phone-layout pin would be unreachable.
  // Cascade is identical to the rule table's `{ xs, sm }` (sm = 600px).
  return {
    flex: '0 0 auto',
    width: '100%',
    minWidth: 0,
    '@media (min-width:600px)': { flex: '1 1 240px', width: 'auto' },
  }
}

export function FormGrid({
  nodes,
  renderNode,
  renderDropZone,
  groupLayout,
  rowGap = 1,
  gridRowAlignItems = 'center',
  fillRemaining = false,
}: FormGridProps) {
  if (isHorizontalGroupLayout(groupLayout)) {
    const sorted = [...nodes].sort((a, b) => a.row - b.row || a.col - b.col)
    const flexSx =
      groupLayout === 'horizontalAlways'
        ? {
            flexDirection: 'row' as const,
            flexWrap: 'nowrap' as const,
            overflowX: 'auto' as const,
          }
        : groupLayout === 'horizontalIfPossible'
          ? { flexDirection: 'row' as const, flexWrap: 'wrap' as const }
          : {
              flexDirection: { xs: 'column' as const, sm: 'row' as const },
              flexWrap: 'wrap' as const,
            }
    return (
      <Box data-form-flow-row sx={{ display: 'flex', gap: rowGap, width: '100%', alignItems: 'flex-start', ...flexSx }}>
        {renderDropZone?.(0)}
        {sorted.map((node, i) => (
          <Fragment key={node.id}>
            <Box sx={horizontalChildSx(node, groupLayout)}>
              {renderNode(node)}
            </Box>
            {renderDropZone?.(i + 1)}
          </Fragment>
        ))}
      </Box>
    )
  }

  const byRow = new Map<number, FormNode[]>()
  for (const node of nodes) {
    const row = node.row
    if (!byRow.has(row)) byRow.set(row, [])
    byRow.get(row)!.push(node)
  }
  const rows = Array.from(byRow.entries()).sort(([a], [b]) => a - b)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: rowGap,
        width: '100%',
        ...(fillRemaining ? { flex: 1, minHeight: 0, overflow: 'hidden', height: '100%' } : null),
      }}
    >
      {rows.map(([rowIndex, rowNodes], i) => (
        <Fragment key={rowIndex}>
          {renderDropZone?.(i)}
          <FormGridRow
            nodes={rowNodes}
            renderNode={renderNode}
            gap={rowGap}
            alignItems={gridRowAlignItems}
            grow={fillRemaining && rowNodes.some((n) => n.type === 'Table' || n.type === 'Pages')}
          />
        </Fragment>
      ))}
      {renderDropZone?.(rows.length)}
    </Box>
  )
}
